/* SeoAuditPanel — admin страница: Lighthouse / Core Web Vitals одит
 *
 * Извиква backend /api/admin/seo-audit?url=... който проксира към
 * Google PageSpeed Insights API. Показва:
 *   • 4 score circles (Performance / A11y / Best Practices / SEO) 0-100
 *   • Lab metrics (LCP, FCP, CLS, TBT, SI, TTI) от Lighthouse run
 *   • Field metrics (LCP, FID, CLS, INP) от реалния CrUX dataset
 *
 * Use cases:
 *   • Pre-deployment audit преди да push-нете нова версия
 *   • Performance regression tracking при бъдещи commit-и
 *   • SEO health check на новопубликувани лендинг страници
 */
import React, { useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Activity, Loader2, AlertCircle, ExternalLink, Smartphone, Monitor } from "lucide-react";

const COMMON_URLS = [
  { label: "Landing (prod)", url: "https://nutramilo.com/" },
  { label: "Landing (preview)", url: "https://endocrine-hub-2.preview.emergentagent.com/" },
  { label: "Pricing", url: "https://nutramilo.com/pricing" },
];

export default function SeoAuditPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();

  const [url, setUrl] = useState(COMMON_URLS[0].url);
  const [strategy, setStrategy] = useState("mobile");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const runAudit = useCallback(async () => {
    if (!url.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await axios.get(`${API}/admin/seo-audit`, {
        params: { url: url.trim(), strategy },
        timeout: 70000,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, url, strategy]);

  return (
    <div className="space-y-5" data-testid="seo-audit-panel">
      {/* Header */}
      <div className={`p-5 rounded-2xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"}`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`p-2.5 rounded-xl ${isDark ? "bg-sky-500/15 text-sky-300" : "bg-sky-100 text-sky-700"}`}>
            <Activity className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              SEO &amp; Performance Audit
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Google PageSpeed Insights · Lighthouse score + Core Web Vitals + CrUX real-user data
            </p>
          </div>
        </div>

        {/* Quick URLs */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {COMMON_URLS.map((q) => (
            <button key={q.url} onClick={() => setUrl(q.url)}
              className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                url === q.url
                  ? (isDark ? "bg-sky-500/20 text-sky-300 border border-sky-500/40" : "bg-sky-100 text-sky-700 border border-sky-300")
                  : (isDark ? "bg-slate-800/50 text-slate-400 hover:bg-slate-800 border border-transparent" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200")
              }`}>
              {q.label}
            </button>
          ))}
        </div>

        {/* URL + strategy */}
        <div className="flex gap-2 flex-wrap">
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            data-testid="seo-audit-url-input"
            className={`flex-1 min-w-[300px] px-3 py-2 rounded-lg border text-sm ${
              isDark ? "bg-[hsl(223,47%,11%)] border-[hsl(223,47%,20%)] text-white" : "bg-white border-slate-200 text-slate-900"
            } focus:outline-none focus:ring-2 ${isDark ? "focus:ring-sky-500/30" : "focus:ring-sky-500/40"}`} />
          <div className={`flex rounded-lg overflow-hidden border ${isDark ? "border-[hsl(223,47%,20%)]" : "border-slate-200"}`}>
            <button onClick={() => setStrategy("mobile")} data-testid="seo-audit-mobile"
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 ${strategy === "mobile"
                ? (isDark ? "bg-sky-500/20 text-sky-300" : "bg-sky-100 text-sky-700")
                : (isDark ? "text-slate-400 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-50")}`}>
              <Smartphone className="w-3.5 h-3.5" /> Mobile
            </button>
            <button onClick={() => setStrategy("desktop")} data-testid="seo-audit-desktop"
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 ${strategy === "desktop"
                ? (isDark ? "bg-sky-500/20 text-sky-300" : "bg-sky-100 text-sky-700")
                : (isDark ? "text-slate-400 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-50")}`}>
              <Monitor className="w-3.5 h-3.5" /> Desktop
            </button>
          </div>
          <button onClick={runAudit} disabled={loading || !url.trim()} data-testid="seo-audit-run"
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1.5 ${
              loading || !url.trim()
                ? (isDark ? "bg-slate-800 text-slate-500" : "bg-slate-100 text-slate-400")
                : "bg-sky-500 hover:bg-sky-600 text-white shadow"
            }`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {loading ? "Анализирам…" : "Стартирай одит"}
          </button>
        </div>
      </div>

      {error && (
        <div className={`p-4 rounded-xl flex items-start gap-3 ${isDark ? "bg-rose-500/10 text-rose-300 border border-rose-500/30" : "bg-rose-50 text-rose-700 border border-rose-200"}`}
             data-testid="seo-audit-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-xs">{error}</div>
        </div>
      )}

      {result && <AuditResult result={result} isDark={isDark} />}
    </div>
  );
}

function AuditResult({ result, isDark }) {
  const scoreColor = (n) => {
    if (n == null) return "#64748B";
    if (n >= 90) return "#10B981";
    if (n >= 50) return "#F59E0B";
    return "#EF4444";
  };

  return (
    <div className="space-y-4" data-testid="seo-audit-result">
      {/* Scores row */}
      <div className={`p-5 rounded-2xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"}`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Performance", val: result.scores.performance, key: "performance" },
            { label: "Accessibility", val: result.scores.accessibility, key: "accessibility" },
            { label: "Best Practices", val: result.scores.best_practices, key: "best_practices" },
            { label: "SEO", val: result.scores.seo, key: "seo" },
          ].map((s) => (
            <div key={s.key} className="flex flex-col items-center" data-testid={`seo-score-${s.key}`}>
              <ScoreCircle value={s.val} color={scoreColor(s.val)} />
              <div className={`text-[11px] font-bold mt-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
        <a href={result.final_url} target="_blank" rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 mt-4 text-[11px] ${isDark ? "text-sky-400" : "text-sky-600"} hover:underline`}>
          {result.final_url} <ExternalLink className="w-3 h-3" />
        </a>
        <span className={`ml-2 text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
          · {result.strategy} · {result.fetched_at ? new Date(result.fetched_at).toLocaleString() : "—"}
        </span>
      </div>

      {/* Lab metrics */}
      <MetricsBlock title="Lab metrics (Lighthouse simulation)" metrics={result.lab} isDark={isDark} testId="seo-lab" />

      {/* Field (CrUX) metrics — only if data exists */}
      {result.field && (result.field.lcp?.percentile != null || result.field.cls?.percentile != null) && (
        <FieldMetricsBlock field={result.field} isDark={isDark} />
      )}
    </div>
  );
}

function ScoreCircle({ value, color }) {
  const display = value == null ? "—" : value;
  const pct = value == null ? 0 : value;
  const C = 2 * Math.PI * 28;
  const offset = C - (C * pct) / 100;
  return (
    <div className="relative w-16 h-16">
      <svg viewBox="0 0 64 64" className="-rotate-90">
        <circle cx="32" cy="32" r="28" stroke="rgba(0,0,0,0.08)" strokeWidth="6" fill="none" />
        <circle cx="32" cy="32" r="28" stroke={color} strokeWidth="6" fill="none"
          strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-base font-bold" style={{ color }}>
        {display}
      </div>
    </div>
  );
}

function MetricsBlock({ title, metrics, isDark, testId }) {
  const rows = [
    { k: "lcp", l: "Largest Contentful Paint", target: "< 2.5 s" },
    { k: "fcp", l: "First Contentful Paint",    target: "< 1.8 s" },
    { k: "cls", l: "Cumulative Layout Shift",   target: "< 0.1" },
    { k: "tbt", l: "Total Blocking Time",       target: "< 200 ms" },
    { k: "si",  l: "Speed Index",               target: "< 3.4 s" },
    { k: "tti", l: "Time to Interactive",       target: "< 3.8 s" },
  ];
  return (
    <div className={`p-5 rounded-2xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"}`}
         data-testid={testId}>
      <div className={`text-xs uppercase font-bold tracking-wider mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {title}
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const m = metrics[r.k] || {};
          const sc = m.score;
          const pillColor = sc == null ? "#64748B" : sc >= 0.9 ? "#10B981" : sc >= 0.5 ? "#F59E0B" : "#EF4444";
          return (
            <div key={r.k} className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{r.l}</div>
                <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>Цел: {r.target}</div>
              </div>
              <div className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-white whitespace-nowrap`}
                   style={{ background: pillColor }}>
                {m.display || "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldMetricsBlock({ field, isDark }) {
  const catColor = (c) => c === "FAST" ? "#10B981" : c === "AVERAGE" ? "#F59E0B" : c === "SLOW" ? "#EF4444" : "#64748B";
  const rows = [
    { k: "lcp", l: "LCP (real users)", unit: "ms" },
    { k: "fid", l: "FID (real users)", unit: "ms" },
    { k: "cls", l: "CLS (real users)", unit: "" },
    { k: "inp", l: "INP (real users)", unit: "ms" },
  ];
  return (
    <div className={`p-5 rounded-2xl border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50"}`}
         data-testid="seo-field">
      <div className={`text-xs uppercase font-bold tracking-wider mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
        Field metrics (CrUX — last 28 days)
      </div>
      <div className={`text-[10px] mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        Реални потребителски данни от Chrome User Experience Report ·{" "}
        Overall: <span className="font-bold" style={{ color: catColor(field.overall_category) }}>
          {field.overall_category || "—"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {rows.map((r) => {
          const v = field[r.k] || {};
          const isCls = r.k === "cls";
          const display = v.percentile == null ? "—" : isCls ? (v.percentile / 100).toFixed(2) : `${Math.round(v.percentile)}${r.unit}`;
          return (
            <div key={r.k} className="text-center">
              <div className={`text-lg font-bold`} style={{ color: catColor(v.category) }}>{display}</div>
              <div className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>{r.l}</div>
              <div className="text-[9px] uppercase font-bold" style={{ color: catColor(v.category) }}>
                {v.category || ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
